// Подразделение и оргструктура (006: FR-001 – FR-011, FR-022а, FR-022б; data-model.md; ADR-0024).
// Подразделение — часть организации, своей или внешней; вложенное, без дат. Основной куратор
// подразделения — его руководитель; руководитель человека считается по дереву и не хранится.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, VersionSchema } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import {
  CuratorshipSchema,
  EmailSchema,
  RecordRefSchema,
  UnitRefSchema,
  WorkplaceSchema,
} from './record.ts';

export const UNIT_NAME_MAX = 200;

export const unitStates = ['active', 'archived'] as const;
export type UnitState = (typeof unitStates)[number];
export const UnitStateSchema = z.enum(unitStates);

export const unitStateLabels = {
  active: 'Действует',
  archived: 'В архиве',
} as const satisfies Record<UnitState, string>;

export const UnitSchema = z.object({
  id: IdSchema,
  organization: RecordRefSchema,
  /** Родитель; null — корень дерева организации. */
  parentId: IdSchema.nullable(),
  name: z.string(),
  state: UnitStateSchema,
  version: VersionSchema,
});
export type Unit = z.infer<typeof UnitSchema>;

/** Строка дерева в карточке организации (FR-009). */
export const UnitNodeSchema = UnitSchema.extend({
  curators: z.number().int().min(0),
  /** Имя — только из областей пользователя (FR-022б); hasPrimaryCurator — есть ли он вообще. */
  primaryCurator: PersonRefSchema.nullable(),
  hasPrimaryCurator: z.boolean(),
  employees: z.number().int().min(0),
  /** Общая почта; без права дополнять — нет (FR-022а). */
  email: z
    .object({ address: z.string(), external: z.boolean(), confirmed: z.boolean() })
    .nullable()
    .optional(),
});
export type UnitNode = z.infer<typeof UnitNodeSchema>;

export const unitAliasSources = ['rename', 'mention'] as const;
export const UnitAliasSourceSchema = z.enum(unitAliasSources);

/** Карточка подразделения (FR-009, FR-022а, FR-022б). */
export const UnitCardSchema = z.object({
  unit: UnitSchema,
  /** Предки от корня. */
  path: z.array(UnitRefSchema),
  children: z.array(UnitNodeSchema),
  /** Ведущему — только люди из его областей; остальные — числом в hidden. */
  curators: z.array(CuratorshipSchema),
  employees: z.array(WorkplaceSchema),
  hidden: z.object({ curators: z.number().int().min(0), employees: z.number().int().min(0) }),
  /** Основной куратор есть, даже если его имя вне областей пользователя (FR-022б). */
  hasPrimaryCurator: z.boolean(),
  /** Общая почта; без права дополнять — нет. */
  email: EmailSchema.nullable().optional(),
  aliases: z.array(z.object({ text: z.string(), source: UnitAliasSourceSchema })),
  /** Ответ «задать общую почту»: тот же адрес у человека — предупреждение, пары нет (FR-012). */
  sameEmail: RecordRefSchema.nullable().optional(),
});
export type UnitCard = z.infer<typeof UnitCardSchema>;

export const UnitNameInputSchema = z
  .string({ error: fieldMessages.unitName })
  .trim()
  .min(2, { error: fieldMessages.unitName })
  .max(UNIT_NAME_MAX, { error: fieldMessages.unitName });

export const CreateUnitBodySchema = z.object({
  organizationId: IdSchema,
  parentId: IdSchema.nullable(),
  name: UnitNameInputSchema,
});
export type CreateUnitBody = z.output<typeof CreateUnitBodySchema>;

export const UpdateUnitBodySchema = z
  .object({ name: UnitNameInputSchema.optional(), parentId: IdSchema.nullable().optional() })
  .refine((v) => v.name !== undefined || v.parentId !== undefined, {
    error: 'Укажите, что изменить: название или родителя',
  });
export type UpdateUnitBody = z.output<typeof UpdateUnitBodySchema>;

export const UnitParamsSchema = z.object({ id: IdSchema });

/** Окно архива (FR-005): числа последствий. */
export const UnitArchivePreviewSchema = z.object({
  units: z.number().int().min(1),
  workplaces: z.number().int().min(0),
  curators: z.number().int().min(0),
  openItems: z.number().int().min(0),
  addresseeChanges: z.number().int().min(0),
});
export type UnitArchivePreview = z.infer<typeof UnitArchivePreviewSchema>;

export const UnitArchiveResultSchema = z.object({
  archiveId: IdSchema,
  units: z.array(UnitSchema),
});
export type UnitArchiveResult = z.infer<typeof UnitArchiveResultSchema>;

/** Общая почта подразделения (FR-011); null — убрать. */
export const SetUnitEmailBodySchema = z.object({ address: z.string().trim().max(254).nullable() });

/** Почему руководитель не определён (FR-008). */
export const managerReasons = ['no_workplace', 'no_curator'] as const;
export const ManagerReasonSchema = z.enum(managerReasons);
export type ManagerReason = (typeof managerReasons)[number];

/** Руководитель человека и почему он (FR-008, FR-009); не хранится. */
export const ManagerSchema = z.object({
  manager: RecordRefSchema.nullable(),
  /** Чей основной куратор: подразделение или организация. */
  via: z
    .object({ kind: z.enum(['unit', 'organization']), id: IdSchema, name: z.string() })
    .nullable(),
  reason: ManagerReasonSchema.nullable(),
  /** Где нет основного куратора — снизу вверх (reason no_curator, research, Р4); иначе пусто. */
  missing: z.array(
    z.object({ kind: z.enum(['unit', 'organization']), id: IdSchema, name: z.string() }),
  ),
  /** Карточка руководителя открывается, только если пользователь её видит (FR-022б). */
  visible: z.boolean(),
});
export type Manager = z.infer<typeof ManagerSchema>;
